import { type ReactNode } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Wallet, FileText, LineChart, BookOpen, LogOut, User } from "lucide-react";
import { Logo } from "./Logo";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { logout, type AuthUser } from "@/lib/api";
import { useSession } from "@/lib/session";

const NAV = [
  { to: "/investidor/posicoes", label: "Minhas posições", icon: Wallet },
  { to: "/investidor/distribuicoes", label: "Distribuições", icon: FileText },
  { to: "/investidor/nav", label: "Histórico NAV", icon: LineChart },
  { to: "/investidor/lamina", label: "Lâmina ANBIMA", icon: BookOpen },
  { to: "/investidor/perfil", label: "Meu perfil", icon: User },
];

export function InvestorShell({ user, children }: { user: AuthUser; children: ReactNode }) {
  const navigate = useNavigate();
  const { setUser } = useSession();

  async function handleLogout() {
    await logout();
    setUser(null);
    navigate("/login", { replace: true });
  }

  return (
    <div className="min-h-screen flex bg-muted/40">
      <aside className="hidden lg:flex w-60 shrink-0 flex-col border-r border-border bg-background">
        <div className="h-16 flex items-center px-5 border-b border-border">
          <Logo />
        </div>
        <nav className="flex-1 p-3 space-y-0.5">
          <div className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
            Portal do Cotista
          </div>
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-muted",
                )
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-4 space-y-2">
          <div className="text-xs leading-tight">
            <div className="font-semibold truncate">{user.full_name}</div>
            <div className="text-muted-foreground truncate">{user.email}</div>
          </div>
          <Button variant="outline" size="sm" className="w-full" onClick={handleLogout}>
            <LogOut className="h-4 w-4" /> Sair
          </Button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <header className="h-14 border-b border-border bg-background flex items-center px-4 lg:px-8 justify-between sticky top-0 z-30">
          <div className="lg:hidden"><Logo /></div>
          <div className="hidden lg:block text-sm text-muted-foreground">Portal do Cotista — CredCapital</div>
          <Button variant="ghost" size="sm" onClick={handleLogout} aria-label="Sair" className="lg:hidden">
            <LogOut className="h-4 w-4" />
          </Button>
        </header>
        <div className="px-4 lg:px-10 py-6 lg:py-8 max-w-6xl mx-auto min-w-0">{children}</div>
      </main>
    </div>
  );
}
